import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { Row, Col } from 'reactstrap';
import {
    RebelColors,
    RebelAccentButton,
    RebelButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB, generateGUID } from '../../../utils/database/firebase-budget-tool';
import { listenToDocument, updateDocumentProperty } from '../../../utils/firestore-functions';
import { getMonthDates, getWeekdayDates, getWeekendDates, getSeasonalRuleForDate } from '../../../utils/date-pricing-utils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const SEASONAL_COLORS = [
    { bg: 'rgba(20, 184, 166, 0.15)', border: '#14b8a6', text: '#14b8a6' },
    { bg: 'rgba(168, 85, 247, 0.15)', border: '#a855f7', text: '#a855f7' },
    { bg: 'rgba(34, 197, 94, 0.15)', border: '#22c55e', text: '#22c55e' },
    { bg: 'rgba(249, 115, 22, 0.15)', border: '#f97316', text: '#f97316' },
    { bg: 'rgba(236, 72, 153, 0.15)', border: '#ec4899', text: '#ec4899' },
    { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#3b82f6' },
];

const getSeasonalColor = (ruleId, allRules) => {
    const index = allRules.findIndex(r => r.id === ruleId);
    return SEASONAL_COLORS[index % SEASONAL_COLORS.length];
};

const RebelVillaPricingTab = ({ villaId }) => {
    const budgetDB = GetDB();

    // Calendar state
    const [currentMonth, setCurrentMonth] = useState(DateTime.now());
    const [selectedDates, setSelectedDates] = useState([]);
    const [lastClickedDate, setLastClickedDate] = useState(null);

    // Villa data
    const [baseRate, setBaseRate] = useState(0);
    const [datePricing, setDatePricing] = useState({});
    const [bookingRules, setBookingRules] = useState({
        minNights: 0,
        maxNights: 0,
        blackoutDates: [],
        callToBookDates: [],
        checkInDays: [],
        checkOutDays: [],
        dateNotes: {},
        nightDiscounts: [],
        seasonalRules: []
    });

    // UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedModal, setSavedModal] = useState(false);
    const [actionPrice, setActionPrice] = useState('');
    const [actionNote, setActionNote] = useState('');
    const [rulesOpen, setRulesOpen] = useState(false);
    const [seasonalRuleModal, setSeasonalRuleModal] = useState(false);
    const [editingSeasonalRule, setEditingSeasonalRule] = useState(null);
    const [seasonalRuleForm, setSeasonalRuleForm] = useState({
        label: '', minNights: 7, maxNights: 0, startDate: '', endDate: ''
    });

    // Inline editing state
    const [editingDate, setEditingDate] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const editingInputRef = useRef(null);
    const isAdvancingRef = useRef(false);

    // Load villa data
    useEffect(() => {
        const unsubscribe = listenToDocument(budgetDB, "rebelVillas", villaId, (villa) => {
            if (villa) {
                setBaseRate(parseFloat(villa.villaRentalFee) || 0);
                setDatePricing(villa.datePricing || {});
                setBookingRules({
                    minNights: villa.bookingRules?.minNights || 0,
                    maxNights: villa.bookingRules?.maxNights || 0,
                    blackoutDates: villa.bookingRules?.blackoutDates || [],
                    callToBookDates: villa.bookingRules?.callToBookDates || [],
                    checkInDays: villa.bookingRules?.checkInDays || [],
                    checkOutDays: villa.bookingRules?.checkOutDays || [],
                    dateNotes: villa.bookingRules?.dateNotes || {},
                    nightDiscounts: villa.bookingRules?.nightDiscounts || [],
                    seasonalRules: villa.bookingRules?.seasonalRules || []
                });
            }
            setLoading(false);
        });
        return () => { if (unsubscribe) unsubscribe(); };
    }, [villaId, budgetDB]);

    // Auto-focus inline editing input
    useEffect(() => {
        if (editingDate && editingInputRef.current) {
            editingInputRef.current.focus();
            editingInputRef.current.select();
        }
    }, [editingDate]);

    // Save all pricing data
    const savePricingData = async () => {
        setSaving(true);
        try {
            await new Promise((resolve, reject) => {
                updateDocumentProperty(budgetDB, "rebelVillas", villaId, "villaRentalFee", baseRate, resolve, reject);
            });
            await new Promise((resolve, reject) => {
                updateDocumentProperty(budgetDB, "rebelVillas", villaId, "datePricing", datePricing, resolve, reject);
            });
            await new Promise((resolve, reject) => {
                updateDocumentProperty(budgetDB, "rebelVillas", villaId, "bookingRules", bookingRules, resolve, reject);
            });
            setSavedModal(true);
        } catch (error) {
            console.error('Error saving pricing data:', error);
            alert('Error saving pricing data. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Calendar navigation
    const prevMonth = () => setCurrentMonth(currentMonth.minus({ months: 1 }));
    const nextMonth = () => setCurrentMonth(currentMonth.plus({ months: 1 }));

    // Build calendar grid for current month
    const getCalendarDays = useCallback(() => {
        const firstDay = currentMonth.startOf('month');
        const lastDay = currentMonth.endOf('month');
        const startDayOfWeek = firstDay.weekday === 7 ? 0 : firstDay.weekday; // Sun=0

        const days = [];
        // Add blank cells for days before the 1st
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }
        // Add actual days
        let current = firstDay;
        while (current <= lastDay) {
            days.push(current.toISODate());
            current = current.plus({ days: 1 });
        }
        return days;
    }, [currentMonth]);

    // Date selection handlers
    const handleDateClick = (dateStr, e) => {
        if (!dateStr) return;

        if (e.shiftKey && lastClickedDate) {
            // Shift+click: select range (exit inline editing)
            setEditingDate(null);
            setActionNote('');
            const start = DateTime.fromISO(lastClickedDate < dateStr ? lastClickedDate : dateStr);
            const end = DateTime.fromISO(lastClickedDate < dateStr ? dateStr : lastClickedDate);
            const range = [];
            let d = start;
            while (d <= end) {
                range.push(d.toISODate());
                d = d.plus({ days: 1 });
            }
            setSelectedDates(range);
        } else if (e.ctrlKey || e.metaKey) {
            // Ctrl/Cmd+click: toggle individual (exit inline editing)
            setEditingDate(null);
            setActionNote('');
            setSelectedDates(prev =>
                prev.includes(dateStr)
                    ? prev.filter(d => d !== dateStr)
                    : [...prev, dateStr]
            );
        } else {
            // Single click: enter inline editing mode
            const status = getDateStatus(dateStr);
            // Pre-populate the note field with existing note for this date
            setActionNote(bookingRules.dateNotes[dateStr] || '');
            if (status.isBlackout || status.isCallToBook) {
                // Don't inline edit blackout/call-to-book dates, just select
                setEditingDate(null);
                setSelectedDates([dateStr]);
            } else {
                setEditingDate(dateStr);
                setEditingValue(datePricing[dateStr] !== undefined ? datePricing[dateStr].toString() : '');
                setSelectedDates([dateStr]);
            }
        }
        setLastClickedDate(dateStr);
    };

    // Quick select helpers
    const selectAllInMonth = () => {
        setSelectedDates(getMonthDates(currentMonth.year, currentMonth.month));
    };
    const selectWeekdays = () => {
        setSelectedDates(getWeekdayDates(currentMonth.year, currentMonth.month));
    };
    const selectWeekends = () => {
        setSelectedDates(getWeekendDates(currentMonth.year, currentMonth.month));
    };
    const clearSelection = () => {
        setSelectedDates([]);
        setActionPrice('');
        setActionNote('');
    };

    // Action handlers for selected dates
    const applyPrice = () => {
        if (actionPrice === '' || selectedDates.length === 0) return;
        const price = parseFloat(actionPrice);
        if (isNaN(price)) return;
        const newPricing = { ...datePricing };
        selectedDates.forEach(dateStr => {
            if (price === baseRate) {
                delete newPricing[dateStr]; // No need to store if same as base
            } else {
                newPricing[dateStr] = price;
            }
        });
        setDatePricing(newPricing);
        setActionPrice('');
    };

    const markBlackout = () => {
        if (selectedDates.length === 0) return;
        const newRules = { ...bookingRules };
        const existing = new Set(newRules.blackoutDates);
        selectedDates.forEach(d => existing.add(d));
        newRules.blackoutDates = [...existing].sort();
        // Remove from callToBook if also marked
        newRules.callToBookDates = newRules.callToBookDates.filter(d => !selectedDates.includes(d));
        setBookingRules(newRules);
    };

    const markCallToBook = () => {
        if (selectedDates.length === 0) return;
        const newRules = { ...bookingRules };
        const existing = new Set(newRules.callToBookDates);
        selectedDates.forEach(d => existing.add(d));
        newRules.callToBookDates = [...existing].sort();
        // Remove from blackout if also marked
        newRules.blackoutDates = newRules.blackoutDates.filter(d => !selectedDates.includes(d));
        setBookingRules(newRules);
    };

    const applyNote = () => {
        if (actionNote.trim() === '' || selectedDates.length === 0) return;
        const newRules = { ...bookingRules };
        const newNotes = { ...newRules.dateNotes };
        selectedDates.forEach(d => { newNotes[d] = actionNote.trim(); });
        newRules.dateNotes = newNotes;
        setBookingRules(newRules);
    };

    const deleteNote = () => {
        if (selectedDates.length === 0) return;
        const newRules = { ...bookingRules };
        const newNotes = { ...newRules.dateNotes };
        selectedDates.forEach(d => { delete newNotes[d]; });
        newRules.dateNotes = newNotes;
        setBookingRules(newRules);
        setActionNote('');
    };

    const clearOverrides = () => {
        if (selectedDates.length === 0) return;
        const newPricing = { ...datePricing };
        const newRules = { ...bookingRules };
        const newNotes = { ...newRules.dateNotes };
        selectedDates.forEach(d => {
            delete newPricing[d];
            delete newNotes[d];
        });
        newRules.blackoutDates = newRules.blackoutDates.filter(d => !selectedDates.includes(d));
        newRules.callToBookDates = newRules.callToBookDates.filter(d => !selectedDates.includes(d));
        newRules.dateNotes = newNotes;
        setDatePricing(newPricing);
        setBookingRules(newRules);
    };

    // Inline editing helpers
    const getNextCalendarDate = (dateStr) => {
        return DateTime.fromISO(dateStr).plus({ days: 1 }).toISODate();
    };

    const saveInlinePrice = (dateStr, value) => {
        if (value === '') return;
        const price = parseFloat(value);
        if (isNaN(price)) return;
        const newPricing = { ...datePricing };
        if (price === baseRate) {
            delete newPricing[dateStr];
        } else {
            newPricing[dateStr] = price;
        }
        setDatePricing(newPricing);
    };

    const advanceToNextDate = (currentDateStr) => {
        const nextDate = getNextCalendarDate(currentDateStr);
        const nextDt = DateTime.fromISO(nextDate);

        // If the next date is in a different month, advance the calendar
        if (nextDt.month !== currentMonth.month || nextDt.year !== currentMonth.year) {
            setCurrentMonth(nextDt.startOf('month'));
        }

        // Skip blackout/call-to-book dates
        const isBlackout = bookingRules.blackoutDates.includes(nextDate);
        const isCallToBook = bookingRules.callToBookDates.includes(nextDate);
        if (isBlackout || isCallToBook) {
            setEditingDate(null);
            setSelectedDates([nextDate]);
            setLastClickedDate(nextDate);
            return;
        }

        setEditingDate(nextDate);
        setEditingValue(datePricing[nextDate] !== undefined ? datePricing[nextDate].toString() : '');
        setSelectedDates([nextDate]);
        setLastClickedDate(nextDate);
    };

    const handleInlineKeyDown = (e, dateStr) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            isAdvancingRef.current = true;
            saveInlinePrice(dateStr, editingValue);
            advanceToNextDate(dateStr);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            isAdvancingRef.current = true;
            setEditingDate(null);
            setEditingValue('');
        }
    };

    const handleInlineBlur = (dateStr) => {
        if (isAdvancingRef.current) {
            isAdvancingRef.current = false;
            return;
        }
        saveInlinePrice(dateStr, editingValue);
        setEditingDate(null);
        setEditingValue('');
    };

    // Night discount handlers
    const addDiscount = () => {
        setBookingRules(prev => ({
            ...prev,
            nightDiscounts: [...prev.nightDiscounts, { minNights: 7, type: 'percentage', value: 10, id: generateGUID() }]
        }));
    };
    const removeDiscount = (index) => {
        setBookingRules(prev => ({
            ...prev,
            nightDiscounts: prev.nightDiscounts.filter((_, i) => i !== index)
        }));
    };
    const updateDiscount = (index, field, value) => {
        setBookingRules(prev => {
            const newDiscounts = [...prev.nightDiscounts];
            newDiscounts[index] = { ...newDiscounts[index], [field]: field === 'type' ? value : parseFloat(value) || 0 };
            return { ...prev, nightDiscounts: newDiscounts };
        });
    };

    // Seasonal rule handlers
    const openCreateSeasonalRule = () => {
        const sorted = [...selectedDates].sort();
        setSeasonalRuleForm({
            label: '',
            minNights: 7,
            maxNights: 0,
            startDate: sorted.length > 0 ? sorted[0] : '',
            endDate: sorted.length > 0 ? sorted[sorted.length - 1] : ''
        });
        setEditingSeasonalRule(null);
        setSeasonalRuleModal(true);
    };

    const openEditSeasonalRule = (rule) => {
        setSeasonalRuleForm({
            label: rule.label,
            minNights: rule.minNights,
            maxNights: rule.maxNights,
            startDate: rule.startDate,
            endDate: rule.endDate
        });
        setEditingSeasonalRule(rule);
        setSeasonalRuleModal(true);
    };

    const getOverlappingRules = () => {
        const { startDate, endDate } = seasonalRuleForm;
        if (!startDate || !endDate) return [];
        const s = startDate <= endDate ? startDate : endDate;
        const e = startDate <= endDate ? endDate : startDate;
        return bookingRules.seasonalRules.filter(rule => {
            if (editingSeasonalRule && rule.id === editingSeasonalRule.id) return false;
            return s <= rule.endDate && e >= rule.startDate;
        });
    };

    const getRuleOverlaps = (rule) => {
        return bookingRules.seasonalRules.filter(other => {
            if (other.id === rule.id) return false;
            return rule.startDate <= other.endDate && rule.endDate >= other.startDate;
        });
    };

    const saveSeasonalRule = () => {
        const { label, minNights, maxNights, startDate, endDate } = seasonalRuleForm;
        if (!startDate || !endDate || !label.trim()) return;
        const sortedStart = startDate <= endDate ? startDate : endDate;
        const sortedEnd = startDate <= endDate ? endDate : startDate;

        if (editingSeasonalRule) {
            setBookingRules(prev => ({
                ...prev,
                seasonalRules: prev.seasonalRules.map(r =>
                    r.id === editingSeasonalRule.id
                        ? { ...r, label: label.trim(), minNights: parseInt(minNights) || 0, maxNights: parseInt(maxNights) || 0, startDate: sortedStart, endDate: sortedEnd }
                        : r
                )
            }));
        } else {
            setBookingRules(prev => ({
                ...prev,
                seasonalRules: [...prev.seasonalRules, {
                    id: generateGUID(),
                    label: label.trim(),
                    minNights: parseInt(minNights) || 0,
                    maxNights: parseInt(maxNights) || 0,
                    startDate: sortedStart,
                    endDate: sortedEnd
                }]
            }));
        }
        setSeasonalRuleModal(false);
    };

    const deleteSeasonalRule = (ruleId) => {
        setBookingRules(prev => ({
            ...prev,
            seasonalRules: prev.seasonalRules.filter(r => r.id !== ruleId)
        }));
    };

    // Check-in/out day toggles
    const toggleCheckInDay = (day) => {
        setBookingRules(prev => {
            const days = prev.checkInDays.includes(day)
                ? prev.checkInDays.filter(d => d !== day)
                : [...prev.checkInDays, day];
            return { ...prev, checkInDays: days };
        });
    };
    const toggleCheckOutDay = (day) => {
        setBookingRules(prev => {
            const days = prev.checkOutDays.includes(day)
                ? prev.checkOutDays.filter(d => d !== day)
                : [...prev.checkOutDays, day];
            return { ...prev, checkOutDays: days };
        });
    };

    // Render helpers
    const getDateStatus = (dateStr) => {
        if (!dateStr) return null;
        const isBlackout = bookingRules.blackoutDates.includes(dateStr);
        const isCallToBook = bookingRules.callToBookDates.includes(dateStr);
        const isOverride = datePricing[dateStr] !== undefined;
        const hasNote = bookingRules.dateNotes[dateStr] !== undefined;
        const isSelected = selectedDates.includes(dateStr);
        const price = isOverride ? datePricing[dateStr] : baseRate;
        const seasonalRule = getSeasonalRuleForDate(dateStr, bookingRules.seasonalRules);
        return { isBlackout, isCallToBook, isOverride, hasNote, isSelected, price, seasonalRule };
    };

    const customDateCount = Object.keys(datePricing).length;
    const blackoutCount = bookingRules.blackoutDates.length;
    const callToBookCount = bookingRules.callToBookDates.length;

    if (loading) {
        return <LoadingContainer><p>Loading pricing data...</p></LoadingContainer>;
    }

    return (
        <React.Fragment>
            <RebelModal isOpen={savedModal} toggle={() => setSavedModal(false)}>
                <RebelModalHeader>Saved</RebelModalHeader>
                <RebelModalBody>Successfully saved date pricing and booking rules.</RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={() => setSavedModal(false)}>Okay</RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>

            <RebelModal isOpen={seasonalRuleModal} toggle={() => setSeasonalRuleModal(false)}>
                <RebelModalHeader>
                    {editingSeasonalRule ? 'Edit Seasonal Rule' : 'Create Seasonal Rule'}
                </RebelModalHeader>
                <RebelModalBody>
                    <ModalFormGroup>
                        <RuleLabel>Rule Name</RuleLabel>
                        <RuleInput
                            type="text"
                            value={seasonalRuleForm.label}
                            onChange={(e) => setSeasonalRuleForm(prev => ({ ...prev, label: e.target.value }))}
                            placeholder="e.g. Summer Peak"
                            style={{ width: '100%' }}
                        />
                    </ModalFormGroup>
                    <ModalFormRow>
                        <ModalFormGroup>
                            <RuleLabel>Start Date</RuleLabel>
                            <RuleInput
                                type="date"
                                value={seasonalRuleForm.startDate}
                                onChange={(e) => setSeasonalRuleForm(prev => ({ ...prev, startDate: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </ModalFormGroup>
                        <ModalFormGroup>
                            <RuleLabel>End Date</RuleLabel>
                            <RuleInput
                                type="date"
                                value={seasonalRuleForm.endDate}
                                onChange={(e) => setSeasonalRuleForm(prev => ({ ...prev, endDate: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </ModalFormGroup>
                    </ModalFormRow>
                    <ModalFormRow>
                        <ModalFormGroup>
                            <RuleLabel>Minimum Nights</RuleLabel>
                            <RuleInput
                                type="number"
                                min="0"
                                value={seasonalRuleForm.minNights || ''}
                                onChange={(e) => setSeasonalRuleForm(prev => ({ ...prev, minNights: parseInt(e.target.value) || 0 }))}
                                placeholder="0"
                                style={{ width: '100%' }}
                            />
                        </ModalFormGroup>
                        <ModalFormGroup>
                            <RuleLabel>Maximum Nights</RuleLabel>
                            <RuleInput
                                type="number"
                                min="0"
                                value={seasonalRuleForm.maxNights || ''}
                                onChange={(e) => setSeasonalRuleForm(prev => ({ ...prev, maxNights: parseInt(e.target.value) || 0 }))}
                                placeholder="0 (no limit)"
                                style={{ width: '100%' }}
                            />
                        </ModalFormGroup>
                    </ModalFormRow>
                    {getOverlappingRules().length > 0 && (
                        <OverlapWarning>
                            <RebelIcon icon={['fas', 'exclamation-triangle']} />
                            Overlaps with: {getOverlappingRules().map(r => r.label).join(', ')}. The most restrictive rule will apply for overlapping dates.
                        </OverlapWarning>
                    )}
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={() => setSeasonalRuleModal(false)}>Cancel</RebelSecondaryButton>
                    <RebelAccentButton
                        onClick={saveSeasonalRule}
                        disabled={!seasonalRuleForm.label.trim() || !seasonalRuleForm.startDate || !seasonalRuleForm.endDate}
                    >
                        {editingSeasonalRule ? 'Save Changes' : 'Create Rule'}
                    </RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>

            <PricingContainer>
                {/* Header */}
                <HeaderRow>
                    <HeaderContent>
                        <SectionTitle>
                            <RebelIcon icon={['fas', 'calendar-alt']} />
                            Date-Based Pricing
                        </SectionTitle>
                        <SectionDescription>
                            Set nightly rates for specific dates. Dates without a custom price use the base rate of <GoldText>${baseRate}</GoldText>/night.
                        </SectionDescription>
                    </HeaderContent>
                    <HeaderStats>
                        <StatBadge>{customDateCount} custom dates</StatBadge>
                        {blackoutCount > 0 && <StatBadge $type="blackout">{blackoutCount} blackout</StatBadge>}
                        {callToBookCount > 0 && <StatBadge $type="callToBook">{callToBookCount} call-to-book</StatBadge>}
                    </HeaderStats>
                </HeaderRow>

                {/* Base Price */}
                <BasePriceSection>
                    <BasePriceLabel>
                        <RebelIcon icon={['fas', 'dollar-sign']} />
                        Base Nightly Rate
                    </BasePriceLabel>
                    <BasePriceSubtext>Default rate for dates without a custom price</BasePriceSubtext>
                    <BasePriceInputGroup>
                        <BasePricePrefix>$</BasePricePrefix>
                        <BasePriceInput
                            type="number"
                            min="0"
                            step="1"
                            value={baseRate || ''}
                            onChange={(e) => setBaseRate(parseFloat(e.target.value) || 0)}
                            placeholder="0"
                        />
                        <BasePriceSuffix>/ night</BasePriceSuffix>
                    </BasePriceInputGroup>
                </BasePriceSection>

                {/* Calendar Section */}
                <CalendarSection>
                    {/* Month Navigation */}
                    <MonthNav>
                        <NavButton onClick={prevMonth}>&lt;</NavButton>
                        <MonthTitle>{currentMonth.toFormat('MMMM yyyy')}</MonthTitle>
                        <NavButton onClick={nextMonth}>&gt;</NavButton>
                    </MonthNav>

                    {/* Quick Select Buttons */}
                    <QuickSelectRow>
                        <QuickBtn onClick={selectAllInMonth}>Entire Month</QuickBtn>
                        <QuickBtn onClick={selectWeekdays}>Weekdays</QuickBtn>
                        <QuickBtn onClick={selectWeekends}>Weekends</QuickBtn>
                        {selectedDates.length > 0 && (
                            <QuickBtn onClick={clearSelection} $clear>Clear ({selectedDates.length})</QuickBtn>
                        )}
                    </QuickSelectRow>

                    {/* Calendar Grid */}
                    <CalendarGrid>
                        {DAY_NAMES.map(day => (
                            <WeekdayHeader key={day}>{day}</WeekdayHeader>
                        ))}
                        {getCalendarDays().map((dateStr, i) => {
                            if (!dateStr) return <EmptyCell key={`empty-${i}`} />;
                            const status = getDateStatus(dateStr);
                            const dt = DateTime.fromISO(dateStr);
                            const isEditing = editingDate === dateStr;
                            return (
                                <DayCell
                                    key={dateStr}
                                    onClick={(e) => handleDateClick(dateStr, e)}
                                    $selected={status.isSelected}
                                    $blackout={status.isBlackout}
                                    $callToBook={status.isCallToBook}
                                    $override={status.isOverride}
                                    $weekend={dt.weekday === 5 || dt.weekday === 6}
                                    $editing={isEditing}
                                    $seasonalColor={status.seasonalRule ? getSeasonalColor(status.seasonalRule.id, bookingRules.seasonalRules) : null}
                                    title={status.hasNote ? bookingRules.dateNotes[dateStr] : (status.seasonalRule ? `${status.seasonalRule.label} (min ${status.seasonalRule.minNights} nights)` : undefined)}
                                >
                                    {status.seasonalRule && (
                                        <SeasonalBand $color={getSeasonalColor(status.seasonalRule.id, bookingRules.seasonalRules)} />
                                    )}
                                    <DayNumber>{dt.day}</DayNumber>
                                    {isEditing ? (
                                        <InlinePriceInput
                                            ref={editingInputRef}
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            onKeyDown={(e) => handleInlineKeyDown(e, dateStr)}
                                            onBlur={() => handleInlineBlur(dateStr)}
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder={baseRate.toString()}
                                        />
                                    ) : (
                                        <DayPrice $blackout={status.isBlackout} $callToBook={status.isCallToBook}>
                                            {status.isBlackout ? 'N/A' : status.isCallToBook ? 'Call' : `$${status.price}`}
                                        </DayPrice>
                                    )}
                                    {status.hasNote && <NoteIndicator>*</NoteIndicator>}
                                </DayCell>
                            );
                        })}
                    </CalendarGrid>
                    <CalendarHint>Click a date to edit its price. Shift+click for range. Ctrl+click to multi-select.</CalendarHint>
                    {bookingRules.seasonalRules.length > 0 && (
                        <SeasonalLegend>
                            {bookingRules.seasonalRules.map(rule => {
                                const color = getSeasonalColor(rule.id, bookingRules.seasonalRules);
                                return (
                                    <SeasonalLegendItem key={rule.id}>
                                        <SeasonalRuleDot $color={color} />
                                        <span>{rule.label}</span>
                                    </SeasonalLegendItem>
                                );
                            })}
                        </SeasonalLegend>
                    )}
                </CalendarSection>

                {/* Action Panel (visible when dates are selected) */}
                {selectedDates.length > 0 && (
                    <ActionPanel>
                        <ActionTitle>{selectedDates.length} date{selectedDates.length > 1 ? 's' : ''} selected</ActionTitle>
                        <ActionRow>
                            <ActionGroup>
                                <ActionLabel>Set Price</ActionLabel>
                                <ActionInputGroup>
                                    <ActionPrefix>$</ActionPrefix>
                                    <ActionInput
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={actionPrice}
                                        onChange={(e) => setActionPrice(e.target.value)}
                                        placeholder={baseRate.toString()}
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyPrice(); }}
                                    />
                                    <ActionApplyBtn onClick={applyPrice}>Apply</ActionApplyBtn>
                                </ActionInputGroup>
                            </ActionGroup>
                            <ActionGroup>
                                <ActionLabel>Mark As</ActionLabel>
                                <ActionBtnRow>
                                    <BlackoutBtn onClick={markBlackout}>Blackout</BlackoutBtn>
                                    <CallToBookBtn onClick={markCallToBook}>Call to Book</CallToBookBtn>
                                </ActionBtnRow>
                            </ActionGroup>
                            <ActionGroup>
                                <ActionLabel>
                                    {selectedDates.length === 1 && bookingRules.dateNotes[selectedDates[0]] ? 'Edit Note' : 'Add Note'}
                                </ActionLabel>
                                <ActionInputGroup>
                                    <ActionNoteInput
                                        type="text"
                                        value={actionNote}
                                        onChange={(e) => setActionNote(e.target.value)}
                                        placeholder="e.g. Local festival"
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyNote(); }}
                                    />
                                    <ActionApplyBtn onClick={applyNote}>
                                        {selectedDates.length === 1 && bookingRules.dateNotes[selectedDates[0]] ? 'Save' : 'Add'}
                                    </ActionApplyBtn>
                                    {selectedDates.some(d => bookingRules.dateNotes[d]) && (
                                        <DeleteNoteBtn onClick={deleteNote}>
                                            <RebelIcon icon={['fas', 'trash']} />
                                        </DeleteNoteBtn>
                                    )}
                                </ActionInputGroup>
                            </ActionGroup>
                            <ActionGroup>
                                <ActionLabel>&nbsp;</ActionLabel>
                                <ClearOverrideBtn onClick={clearOverrides}>Clear Overrides</ClearOverrideBtn>
                            </ActionGroup>
                            <ActionGroup>
                                <ActionLabel>Seasonal</ActionLabel>
                                <SeasonalRuleBtn onClick={openCreateSeasonalRule}>
                                    <RebelIcon icon={['fas', 'calendar-plus']} /> Create Rule
                                </SeasonalRuleBtn>
                            </ActionGroup>
                        </ActionRow>
                    </ActionPanel>
                )}

                {/* Booking Rules Section */}
                <RulesSection>
                    <RulesHeader onClick={() => setRulesOpen(!rulesOpen)}>
                        <RulesTitle>
                            <RebelIcon icon={['fas', 'cog']} />
                            Booking Rules
                        </RulesTitle>
                        <RebelIcon icon={['fas', rulesOpen ? 'chevron-up' : 'chevron-down']} />
                    </RulesHeader>

                    {rulesOpen && (
                        <RulesBody>
                            {/* Min/Max Nights */}
                            <RulesRow>
                                <RuleGroup>
                                    <RuleLabel>Default Minimum Nights <RuleMuted>(fallback when no seasonal rule applies)</RuleMuted></RuleLabel>
                                    <RuleInput
                                        type="number"
                                        min="0"
                                        value={bookingRules.minNights || ''}
                                        onChange={(e) => setBookingRules(prev => ({ ...prev, minNights: parseInt(e.target.value) || 0 }))}
                                        placeholder="0"
                                    />
                                </RuleGroup>
                                <RuleGroup>
                                    <RuleLabel>Default Maximum Nights <RuleMuted>(fallback when no seasonal rule applies)</RuleMuted></RuleLabel>
                                    <RuleInput
                                        type="number"
                                        min="0"
                                        value={bookingRules.maxNights || ''}
                                        onChange={(e) => setBookingRules(prev => ({ ...prev, maxNights: parseInt(e.target.value) || 0 }))}
                                        placeholder="0 (no limit)"
                                    />
                                </RuleGroup>
                            </RulesRow>

                            {/* Check-in Day Restrictions */}
                            <RulesRow>
                                <RuleGroup $wide>
                                    <RuleLabel>Check-in Days Allowed <RuleMuted>(empty = any day)</RuleMuted></RuleLabel>
                                    <DayToggleRow>
                                        {DAY_NAMES.map((name, i) => (
                                            <DayToggle
                                                key={`ci-${i}`}
                                                $active={bookingRules.checkInDays.includes(i)}
                                                onClick={() => toggleCheckInDay(i)}
                                            >{name}</DayToggle>
                                        ))}
                                    </DayToggleRow>
                                </RuleGroup>
                            </RulesRow>
                            <RulesRow>
                                <RuleGroup $wide>
                                    <RuleLabel>Check-out Days Allowed <RuleMuted>(empty = any day)</RuleMuted></RuleLabel>
                                    <DayToggleRow>
                                        {DAY_NAMES.map((name, i) => (
                                            <DayToggle
                                                key={`co-${i}`}
                                                $active={bookingRules.checkOutDays.includes(i)}
                                                onClick={() => toggleCheckOutDay(i)}
                                            >{name}</DayToggle>
                                        ))}
                                    </DayToggleRow>
                                </RuleGroup>
                            </RulesRow>

                            {/* Night Discounts */}
                            <RulesRow>
                                <RuleGroup $wide>
                                    <RuleLabel>Night-Count Discounts</RuleLabel>
                                    {bookingRules.nightDiscounts.map((discount, i) => (
                                        <DiscountRow key={discount.id || i}>
                                            <DiscountInput
                                                type="number"
                                                min="1"
                                                value={discount.minNights || ''}
                                                onChange={(e) => updateDiscount(i, 'minNights', e.target.value)}
                                                placeholder="7"
                                            />
                                            <DiscountLabel>+ nights</DiscountLabel>
                                            <DiscountSelect
                                                value={discount.type}
                                                onChange={(e) => updateDiscount(i, 'type', e.target.value)}
                                            >
                                                <option value="percentage">% off</option>
                                                <option value="flat">$ off</option>
                                            </DiscountSelect>
                                            <DiscountInput
                                                type="number"
                                                min="0"
                                                value={discount.value || ''}
                                                onChange={(e) => updateDiscount(i, 'value', e.target.value)}
                                                placeholder="10"
                                            />
                                            <DiscountDeleteBtn onClick={() => removeDiscount(i)}>
                                                <RebelIcon icon={['fas', 'times']} />
                                            </DiscountDeleteBtn>
                                        </DiscountRow>
                                    ))}
                                    <AddDiscountBtn onClick={addDiscount}>
                                        + Add Discount Tier
                                    </AddDiscountBtn>
                                </RuleGroup>
                            </RulesRow>

                            {/* Seasonal Rules */}
                            <RulesRow>
                                <RuleGroup $wide>
                                    <RuleLabel>
                                        Seasonal Booking Rules
                                        <RuleMuted> (override min/max nights for date ranges)</RuleMuted>
                                    </RuleLabel>
                                    {bookingRules.seasonalRules.length === 0 && (
                                        <SeasonalEmptyText>No seasonal rules configured. Select dates on the calendar and click "Create Rule" to add one.</SeasonalEmptyText>
                                    )}
                                    {bookingRules.seasonalRules.map((rule) => {
                                        const color = getSeasonalColor(rule.id, bookingRules.seasonalRules);
                                        const overlaps = getRuleOverlaps(rule);
                                        return (
                                            <SeasonalRuleRow key={rule.id} $color={color}>
                                                <SeasonalRuleDot $color={color} />
                                                <SeasonalRuleInfo>
                                                    <SeasonalRuleName>{rule.label}</SeasonalRuleName>
                                                    <SeasonalRuleDates>
                                                        {DateTime.fromISO(rule.startDate).toFormat('MMM d, yyyy')} – {DateTime.fromISO(rule.endDate).toFormat('MMM d, yyyy')}
                                                    </SeasonalRuleDates>
                                                    <SeasonalRuleNights>
                                                        Min: {rule.minNights || 'none'} | Max: {rule.maxNights || 'no limit'}
                                                    </SeasonalRuleNights>
                                                    {overlaps.length > 0 && (
                                                        <SeasonalOverlapNotice>
                                                            <RebelIcon icon={['fas', 'exclamation-triangle']} />
                                                            Overlaps with {overlaps.map(r => r.label).join(', ')}
                                                        </SeasonalOverlapNotice>
                                                    )}
                                                </SeasonalRuleInfo>
                                                <SeasonalRuleActions>
                                                    <SeasonalEditBtn onClick={() => openEditSeasonalRule(rule)}>
                                                        <RebelIcon icon={['fas', 'edit']} /> Edit
                                                    </SeasonalEditBtn>
                                                    <SeasonalDeleteBtn onClick={() => deleteSeasonalRule(rule.id)}>
                                                        <RebelIcon icon={['fas', 'trash-alt']} /> Delete
                                                    </SeasonalDeleteBtn>
                                                </SeasonalRuleActions>
                                            </SeasonalRuleRow>
                                        );
                                    })}
                                    <AddDiscountBtn onClick={openCreateSeasonalRule}>
                                        + Add Seasonal Rule
                                    </AddDiscountBtn>
                                </RuleGroup>
                            </RulesRow>
                        </RulesBody>
                    )}
                </RulesSection>

                {/* Footer / Save */}
                <FooterRow>
                    <FooterHint>
                        <RebelIcon icon={['fas', 'info-circle']} />
                        Pricing and rules are saved to the property template. Experiences inherit them when the property is selected.
                    </FooterHint>
                    <RebelAccentButton onClick={savePricingData} disabled={saving}>
                        <RebelIcon icon={['fas', 'save']} />
                        {saving ? 'Saving...' : 'Save Pricing & Rules'}
                    </RebelAccentButton>
                </FooterRow>
            </PricingContainer>
        </React.Fragment>
    );
};

export default RebelVillaPricingTab;

// ============== STYLED COMPONENTS ==============

const LoadingContainer = styled.div`
    text-align: center;
    padding: 48px;
    color: ${RebelColors.primaryMuted};
`;

const PricingContainer = styled.div`
    padding: 24px;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid ${RebelColors.gold}40;
    gap: 20px;
    flex-wrap: wrap;
`;

const HeaderContent = styled.div`
    flex: 1;
    min-width: 200px;
`;

const SectionTitle = styled.h3`
    color: ${RebelColors.gold};
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    svg { color: ${RebelColors.gold}; }
`;

const SectionDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const GoldText = styled.span`
    color: ${RebelColors.gold};
    font-weight: 600;
`;

const HeaderStats = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const StatBadge = styled.div`
    background: ${props => {
        if (props.$type === 'blackout') return 'rgba(239, 68, 68, 0.15)';
        if (props.$type === 'callToBook') return 'rgba(96, 165, 250, 0.15)';
        return `${RebelColors.gold}15`;
    }};
    border: 1px solid ${props => {
        if (props.$type === 'blackout') return '#ef4444';
        if (props.$type === 'callToBook') return '#60a5fa';
        return RebelColors.gold;
    }};
    color: ${props => {
        if (props.$type === 'blackout') return '#ef4444';
        if (props.$type === 'callToBook') return '#60a5fa';
        return RebelColors.gold;
    }};
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
`;

// ============== CALENDAR ==============

const CalendarSection = styled.div`
    margin-bottom: 24px;
`;

const MonthNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    margin-bottom: 16px;
`;

const NavButton = styled.button`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    width: 36px;
    height: 36px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.2s;
    &:hover {
        border-color: ${RebelColors.gold};
        color: ${RebelColors.gold};
    }
`;

const MonthTitle = styled.span`
    color: ${RebelColors.text};
    font-size: 18px;
    font-weight: 600;
    min-width: 200px;
    text-align: center;
`;

const QuickSelectRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
`;

const QuickBtn = styled.button`
    background: ${props => props.$clear ? 'rgba(239, 68, 68, 0.1)' : RebelColors.surface};
    border: 1px solid ${props => props.$clear ? '#ef4444' : RebelColors.border};
    color: ${props => props.$clear ? '#ef4444' : RebelColors.text};
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s;
    &:hover {
        border-color: ${props => props.$clear ? '#ef4444' : RebelColors.gold};
        color: ${props => props.$clear ? '#ef4444' : RebelColors.gold};
    }
`;

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
`;

const WeekdayHeader = styled.div`
    text-align: center;
    color: ${RebelColors.gold};
    font-size: 12px;
    font-weight: 600;
    padding: 8px 0;
    text-transform: uppercase;
`;

const EmptyCell = styled.div``;

const DayCell = styled.div`
    position: relative;
    background: ${props => {
        if (props.$editing) return `${RebelColors.gold}20`;
        if (props.$selected) return `${RebelColors.gold}30`;
        if (props.$blackout) return 'rgba(239, 68, 68, 0.15)';
        if (props.$callToBook) return 'rgba(96, 165, 250, 0.12)';
        if (props.$seasonalColor) return props.$seasonalColor.bg;
        return RebelColors.surface;
    }};
    border: 2px solid ${props => {
        if (props.$editing) return RebelColors.gold;
        if (props.$selected) return RebelColors.gold;
        if (props.$blackout) return '#ef4444';
        if (props.$callToBook) return '#60a5fa';
        if (props.$override) return `${RebelColors.gold}60`;
        return RebelColors.border;
    }};
    border-radius: 8px;
    padding: 8px 6px;
    cursor: pointer;
    text-align: center;
    min-height: 62px;
    transition: all 0.15s;
    user-select: none;
    ${props => props.$editing && `box-shadow: 0 0 8px ${RebelColors.gold}40;`}

    &:hover {
        border-color: ${RebelColors.gold};
        transform: translateY(-1px);
    }
`;

const DayNumber = styled.div`
    color: ${RebelColors.text};
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
`;

const DayPrice = styled.div`
    color: ${props => {
        if (props.$blackout) return '#ef4444';
        if (props.$callToBook) return '#60a5fa';
        return RebelColors.primaryMuted;
    }};
    font-size: 11px;
    margin-top: 4px;
    font-weight: 500;
`;

const NoteIndicator = styled.span`
    position: absolute;
    top: 2px;
    right: 4px;
    color: ${RebelColors.gold};
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
`;

const CalendarHint = styled.div`
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
    margin-top: 8px;
    text-align: center;
`;

// ============== ACTION PANEL ==============

const ActionPanel = styled.div`
    background: ${RebelColors.surface};
    border: 2px solid ${RebelColors.gold};
    border-radius: 10px;
    padding: 16px 20px;
    margin-bottom: 24px;
`;

const ActionTitle = styled.div`
    color: ${RebelColors.gold};
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
`;

const ActionRow = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    align-items: flex-end;
`;

const ActionGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ActionLabel = styled.label`
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ActionInputGroup = styled.div`
    display: flex;
    align-items: center;
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    border-radius: 6px;
    overflow: hidden;
    &:focus-within {
        border-color: ${RebelColors.gold};
    }
`;

const ActionPrefix = styled.span`
    color: ${RebelColors.gold};
    font-size: 13px;
    font-weight: 600;
    padding: 0 8px;
`;

const ActionInput = styled.input`
    background: transparent;
    border: none;
    color: ${RebelColors.text};
    padding: 8px 4px;
    font-size: 13px;
    width: 80px;
    &:focus { outline: none; }
    &::placeholder { color: ${RebelColors.primaryMuted}; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
`;

const ActionNoteInput = styled.input`
    background: transparent;
    border: none;
    color: ${RebelColors.text};
    padding: 8px;
    font-size: 13px;
    width: 160px;
    &:focus { outline: none; }
    &::placeholder { color: ${RebelColors.primaryMuted}; }
`;

const ActionApplyBtn = styled.button`
    background: ${RebelColors.gold};
    border: none;
    color: #000;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    &:hover { background: #d4af37; }
`;

const ActionBtnRow = styled.div`
    display: flex;
    gap: 6px;
`;

const BlackoutBtn = styled.button`
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid #ef4444;
    color: #ef4444;
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: rgba(239, 68, 68, 0.3); }
`;

const CallToBookBtn = styled.button`
    background: rgba(96, 165, 250, 0.15);
    border: 1px solid #60a5fa;
    color: #60a5fa;
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: rgba(96, 165, 250, 0.3); }
`;

const ClearOverrideBtn = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.primaryMuted};
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        border-color: ${RebelColors.text};
        color: ${RebelColors.text};
    }
`;

// ============== BOOKING RULES ==============

const RulesSection = styled.div`
    border: 1px solid ${RebelColors.border};
    border-radius: 10px;
    margin-bottom: 24px;
    overflow: hidden;
`;

const RulesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: ${RebelColors.surface};
    cursor: pointer;
    transition: background 0.2s;
    &:hover { background: ${RebelColors.surfaceLight}; }
    svg { color: ${RebelColors.gold}; font-size: 14px; }
`;

const RulesTitle = styled.div`
    color: ${RebelColors.gold};
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const RulesBody = styled.div`
    padding: 20px;
    border-top: 1px solid ${RebelColors.border};
`;

const RulesRow = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    &:last-child { margin-bottom: 0; }
`;

const RuleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${props => props.$wide && 'flex: 1; min-width: 100%;'}
`;

const RuleLabel = styled.label`
    color: ${RebelColors.text};
    font-size: 13px;
    font-weight: 600;
`;

const RuleMuted = styled.span`
    color: ${RebelColors.primaryMuted};
    font-weight: 400;
    font-size: 12px;
`;

const RuleInput = styled.input`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    color-scheme: dark;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    width: 140px;
    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
    }
    &::placeholder { color: ${RebelColors.primaryMuted}; }
    &::-webkit-calendar-picker-indicator {
        filter: brightness(0.85) sepia(1) saturate(8) hue-rotate(5deg);
        cursor: pointer;
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
`;

const DayToggleRow = styled.div`
    display: flex;
    gap: 6px;
`;

const DayToggle = styled.button`
    width: 48px;
    height: 36px;
    border-radius: 6px;
    border: 1px solid ${props => props.$active ? RebelColors.gold : RebelColors.border};
    background: ${props => props.$active ? `${RebelColors.gold}25` : 'transparent'};
    color: ${props => props.$active ? RebelColors.gold : RebelColors.primaryMuted};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        border-color: ${RebelColors.gold};
        color: ${RebelColors.gold};
    }
`;

const DiscountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const DiscountInput = styled.input`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    width: 80px;
    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
`;

const DiscountLabel = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    white-space: nowrap;
`;

const DiscountSelect = styled.select`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
    }
`;

const DiscountDeleteBtn = styled.button`
    background: transparent;
    border: 1px solid #ef4444;
    color: #ef4444;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    &:hover { background: rgba(239, 68, 68, 0.15); }
    svg { font-size: 12px; color: #ef4444; }
`;

const AddDiscountBtn = styled.button`
    background: transparent;
    border: 1px dashed ${RebelColors.border};
    color: ${RebelColors.gold};
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        border-color: ${RebelColors.gold};
        background: ${RebelColors.gold}10;
    }
`;

// ============== FOOTER ==============

const FooterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    gap: 16px;
    flex-wrap: wrap;
`;

const FooterHint = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    svg { color: ${RebelColors.gold}; font-size: 14px; }
`;

// ============== BASE PRICE SECTION ==============

const BasePriceSection = styled.div`
    background: ${RebelColors.surface};
    border: 2px solid ${RebelColors.gold};
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    text-align: center;
`;

const BasePriceLabel = styled.div`
    color: ${RebelColors.gold};
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 4px;
    svg { color: ${RebelColors.gold}; }
`;

const BasePriceSubtext = styled.div`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    margin-bottom: 12px;
`;

const BasePriceInputGroup = styled.div`
    display: inline-flex;
    align-items: center;
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    overflow: hidden;
    &:focus-within {
        border-color: ${RebelColors.gold};
        box-shadow: 0 0 0 2px ${RebelColors.gold}30;
    }
`;

const BasePricePrefix = styled.span`
    color: ${RebelColors.gold};
    font-size: 18px;
    font-weight: 700;
    padding: 0 10px;
`;

const BasePriceInput = styled.input`
    background: transparent;
    border: none;
    color: ${RebelColors.text};
    padding: 10px 4px;
    font-size: 18px;
    font-weight: 600;
    width: 120px;
    text-align: center;
    &:focus { outline: none; }
    &::placeholder { color: ${RebelColors.primaryMuted}; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
`;

const BasePriceSuffix = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    padding: 0 12px;
    white-space: nowrap;
`;

// ============== INLINE EDITING ==============

const InlinePriceInput = styled.input`
    background: rgba(0, 0, 0, 0.3);
    border: none;
    border-bottom: 2px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    font-size: 11px;
    font-weight: 600;
    width: 48px;
    text-align: center;
    padding: 2px 0;
    margin-top: 2px;
    &:focus {
        outline: none;
        box-shadow: 0 0 6px ${RebelColors.gold}40;
    }
    &::placeholder { color: ${RebelColors.primaryMuted}; font-size: 10px; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
`;

const DeleteNoteBtn = styled.button`
    background: transparent;
    border: none;
    color: #ef4444;
    padding: 8px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s;
    &:hover { color: #f87171; background: rgba(239, 68, 68, 0.1); }
    svg { font-size: 12px; color: #ef4444; }
`;

// ============== SEASONAL RULES ==============

const SeasonalBand = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$color?.border || '#14b8a6'};
    border-radius: 0 0 6px 6px;
    opacity: 0.8;
`;

const SeasonalRuleBtn = styled.button`
    background: rgba(20, 184, 166, 0.15);
    border: 1px solid #14b8a6;
    color: #14b8a6;
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
    &:hover { background: rgba(20, 184, 166, 0.3); }
    svg { font-size: 12px; color: #14b8a6; }
`;

const SeasonalRuleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    margin-bottom: 8px;
    background: ${props => props.$color?.bg || 'rgba(20, 184, 166, 0.08)'};
    border: 1px solid ${props => (props.$color?.border || '#14b8a6') + '40'};
    border-radius: 8px;
    transition: all 0.2s;
    &:hover {
        border-color: ${props => props.$color?.border || '#14b8a6'};
    }
`;

const SeasonalRuleDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.$color?.border || '#14b8a6'};
    flex-shrink: 0;
`;

const SeasonalRuleInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SeasonalRuleName = styled.span`
    color: ${RebelColors.text};
    font-size: 13px;
    font-weight: 600;
`;

const SeasonalRuleDates = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
`;

const SeasonalRuleNights = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
`;

const SeasonalRuleActions = styled.div`
    display: flex;
    gap: 6px;
    flex-shrink: 0;
`;

const SeasonalEditBtn = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.gold}50;
    color: ${RebelColors.gold};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: all 0.2s;
    &:hover {
        border-color: ${RebelColors.gold};
        background: ${RebelColors.gold}15;
    }
    svg { font-size: 11px; color: ${RebelColors.gold}; }
`;

const SeasonalDeleteBtn = styled.button`
    background: transparent;
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #ef4444;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: all 0.2s;
    &:hover {
        border-color: #ef4444;
        background: rgba(239, 68, 68, 0.1);
    }
    svg { font-size: 11px; color: #ef4444; }
`;

const SeasonalEmptyText = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    font-style: italic;
    margin: 4px 0;
`;

const SeasonalOverlapNotice = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    color: #fbbf24;
    font-size: 11px;
    margin-top: 2px;
    svg { font-size: 10px; color: #fbbf24; }
`;

const SeasonalLegend = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 8px;
    justify-content: center;
    flex-wrap: wrap;
`;

const SeasonalLegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
`;

const ModalFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
    flex: 1;
`;

const ModalFormRow = styled.div`
    display: flex;
    gap: 16px;
`;

const OverlapWarning = styled.div`
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: 8px;
    padding: 10px 14px;
    color: #fbbf24;
    font-size: 12px;
    line-height: 1.4;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    svg { color: #fbbf24; flex-shrink: 0; margin-top: 1px; }
`;
